import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormGroupDirective,
  NgForm,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import {
  BottomSheetItem,
  BottomSheetSelectorComponent,
  BottomSheetSelectorData,
  CommandAvailability,
  CuttingButt,
  CuttingMaterialBaseDialogComponent,
  CuttingMaterialBaseDialogData,
  CuttingPanelHelper,
  Guid,
  LegacyDialogsService,
  MaterialBaseButtsDialogComponent,
  MaterialBaseEdgeDialogData,
  PanelUtilsHelper,
  PresetSettings,
  maxNumberPrecision,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subject, lastValueFrom, of } from 'rxjs';
import { debounceTime, delay, filter, finalize, map, switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  CuttingClientOptions,
  ImportCsvSaveModeEnum,
  PanelTableField,
} from '../../models/cutting/cutting-client.model';
import { CuttingMaterial, MaterialType } from '../../models/cutting/cutting-material.model';
import { AngleMode } from '../../models/cutting/cutting-panel-outline.model';
import { CuttingPanel } from '../../models/cutting/cutting-panel.model';
import { CuttingSettings, PanelSettings, PostformingSideEnum } from '../../models/user/cutting-settings.model';
import { DialogsService } from '../../services/dialogs.service';
import { EventManagerService } from '../../services/event-manager.service';
import { CustomValidators } from '../custom-forms/custom-forms.module';
import { CsvSchemaSelectorDialogComponent } from './csv-schema-selector-dialog/csv-schema-selector-dialog.component';
import { CuttingPanelListService } from './cutting-panel-list.service';
import {
  FaceBandingReplacementCustomDialogComponent,
  FaceBandingReplacementData,
} from './face-banding-replacement-custom-dialog/face-banding-replacement-custom-dialog.component';
import { FieldSettingsComponent } from './field-settings/field-settings.component';
import { ImportFromCsvDialogComponent } from './import-from-csv-dialog/import-from-csv-dialog.component';
import { PanelListDataService } from './panel-list-data.service';
import { PanelMirroringDialogComponent } from './panel-mirroring-dialog/panel-mirroring-dialog.component';
import {
  OutlineEditorTabsEnum,
  PanelOutlineEditorDialogComponent,
} from './panel-outline-editor-dialog/panel-outline-editor-dialog.component';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && control.invalid);
  }
}

@Component({
  selector: 'csl-panel-list-v2',
  templateUrl: 'panel-list-v2.component.html',
  styleUrls: ['panel-list-v2.component.scss'],
  providers: [{ provide: ErrorStateMatcher, useClass: MyErrorStateMatcher }],
})
export class PanelListV2Component implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  private displayedColumns = [
    'position',
    'name',
    'height',
    'width',
    'count',
    'orientation',
    'l1',
    'l2',
    'w1',
    'w2',
    'editedAngles',
    'slotCount',
    'fastenersExists',
    'attachment',
    'faceBandingExists',
    'outlineEditor',
    'remark',
    'delete',
  ];

  form: UntypedFormGroup;

  currentPanelsState = false;
  thisOutlineEditorTabsEnum = OutlineEditorTabsEnum;
  fieldSettingsDialogRef: MatDialogRef<FieldSettingsComponent>;
  outlineEditorDialogRef: MatDialogRef<PanelOutlineEditorDialogComponent>;
  panelMirroringDialogRef: MatDialogRef<PanelMirroringDialogComponent>;
  panelsImportCsvDialogRef: MatDialogRef<ImportFromCsvDialogComponent>;
  private unsubscribeAll$: Subject<void> = new Subject();
  currentScreenWidth = '';
  editPanelPosition = true;
  selection: SelectionModel<string> = new SelectionModel(true);

  postforming: PostformingSideEnum = PostformingSideEnum.None;
  materialBaseButtsDialogRef: MatDialogRef<MaterialBaseButtsDialogComponent>;
  materialGroupsWithPostforming: Map<number, PostformingSideEnum> = new Map();
  materialEditableWidth: Set<number> = new Set();

  csvExport$: Observable<Blob>;

  private _panels: CuttingPanel[] = [];

  @Input() adminMode = false;
  @Input() orderId: number;
  @Input() mainMaterial: CuttingMaterial;
  @Input() materialWidth = 0;
  @Input() panelList: CuttingPanel[];
  @Input() editableOrder = false;
  @Input() inMatBaseIdMaterial = 0;
  @Input() cuttingSettings: CuttingSettings;
  @Input() presetSettings: PresetSettings;
  @Input() clientOptions: CuttingClientOptions;

  @Output() panelListChange: EventEmitter<CuttingPanel[]> = new EventEmitter();
  @Output() setPosInOrder: EventEmitter<void> = new EventEmitter();
  @Output() setMissingPositions: EventEmitter<string | void> = new EventEmitter();

  @Output() valid: EventEmitter<boolean> = new EventEmitter();
  @Output() selectionModeChange: BehaviorSubject<boolean> = new BehaviorSubject(false);
  @Output() cloneSelectedPanels: Subject<CuttingPanel[]> = new Subject();
  @Output() get panelFileAttached(): Subject<File> {
    return this.cuttingPanelListService.onPanelFileAttached;
  }

  @ViewChild(MatTable) table: MatTable<any>;

  get panelSetHasAttachments(): boolean {
    return this._panels.some(p => p.attachmentName > '');
  }

  get panelSetHasButts(): boolean {
    return this._panels.some(p => this.hasButts(p));
  }

  get selectionMode(): boolean {
    return this.selectionModeChange.value;
  }

  get maxNumberPrecision(): number {
    return this.cuttingSettings?.panels.maxNumberPrecision || 0;
  }

  get panelsForm(): UntypedFormArray {
    return this.form.get('panels') as UntypedFormArray;
  }

  get availabilityCommands(): CommandAvailability {
    return this.presetSettings.componentsAvailability.commands;
  }

  get ignoreAllowanceWnenRecalculatingPanelSize(): boolean {
    return this.cuttingSettings.ignoreAllowanceWnenRecalculatingPanelSize;
  }

  constructor(
    private eventManagerService: EventManagerService,
    private cuttingPanelListService: CuttingPanelListService,
    public dialog: MatDialog,
    private _formBuilder: UntypedFormBuilder,
    private panelListDataService: PanelListDataService,
    private _bottomSheet: MatBottomSheet,
    private snackBar: MatSnackBar,
    private legacyDialogsService: LegacyDialogsService,
    private dialogsService: DialogsService,
    private breakpointObserver: BreakpointObserver
  ) {
    this.cuttingPanelListService.onCuttingOptionsChanged
      .asObservable()
      .pipe(
        filter(res => !!res),
        takeUntil(this.unsubscribeAll$)
      )
      .subscribe(response => {
        this.materialGroupsWithPostforming = new Map(
          response.postforming
            .filter(m => m.postformingSide !== PostformingSideEnum.None)
            .map(m => [m.inMatBaseId, m.postformingSide])
        );
        this.materialEditableWidth = new Set(
          response.postforming.filter(group => group.editableWidth).map(group => group.inMatBaseId)
        );
      });

    this.form = this._formBuilder.group({
      panels: new UntypedFormArray([]),
    });

    this.form.statusChanges
      .pipe(debounceTime(300))
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe(() => {
        this.valid.emit(this.form.valid);
      });

    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
      .pipe(
        takeUntil(this.unsubscribeAll$),
        filter(result => result.matches),
        map(result => {
          if (result.breakpoints[Breakpoints.XSmall]) return 'xs';
          if (result.breakpoints[Breakpoints.Small]) return 'sm';
          if (result.breakpoints[Breakpoints.Medium]) return 'md';
          if (result.breakpoints[Breakpoints.Large]) return 'lg';
          if (result.breakpoints[Breakpoints.XLarge]) return 'xl';
        })
      )
      .subscribe(screenWidth => {
        if (screenWidth !== this.currentScreenWidth) {
          this.currentScreenWidth = screenWidth;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!_.isNil(changes.panelList?.currentValue)) {
      this.setPanels([]);
      this.setPanels(changes.panelList.currentValue);
      this.currentPanelsState = this.getCurrentPanelsState();
    }

    if (changes?.cuttingSettings?.currentValue) {
      this.cuttingPanelListService.onCuttingOptionsChanged.next(this.cuttingSettings);
    }

    if (changes?.clientOptions?.currentValue) {
      this.cuttingPanelListService.onClientOptionsChanged.next(this.clientOptions);
    }
  }

  getCurrentPanelsState(): boolean {
    return this._panels && this._panels.every(Boolean);
  }

  private setPanels(val: CuttingPanel[]): void {
    this._panels = val;
    const formArray = new UntypedFormArray(val.map(v => this.panel2FormGroup(v)));
    this.form.setControl('panels', formArray);
  }

  private panel2FormGroup(val: CuttingPanel): UntypedFormGroup {
    const group = new UntypedFormGroup(
      {
        uuid: new UntypedFormControl(val.uuid, Validators.required),
        height: new UntypedFormControl(val.height, [
          Validators.required,
          CustomValidators.number,
          maxNumberPrecision(this.maxNumberPrecision),
        ]),
        width: new UntypedFormControl(val.width, [
          Validators.required,
          CustomValidators.number,
          maxNumberPrecision(this.maxNumberPrecision),
        ]),
        name: new UntypedFormControl(val.name, [Validators.maxLength(500)]),
        position: new UntypedFormControl(val.position, [Validators.required, this.uniquePosValidator()]),
        orientation: new UntypedFormControl(val.orientation, Validators.required),
        count: new UntypedFormControl(val.count, [
          Validators.required,
          CustomValidators.range([1, 500]),
          CustomValidators.number,
        ]),
        remark: new UntypedFormControl(val.remark, [Validators.maxLength(2000)]),
        l1: new UntypedFormControl(val.l1, []),
        l2: new UntypedFormControl(val.l2, []),
        w1: new UntypedFormControl(val.w1, []),
        w2: new UntypedFormControl(val.w2, []),
        attachmentName: new UntypedFormControl(val.attachmentName, []),
      },
      { validators: [this.sizesValidator(val.uuid, 'height', 'width')] }
    );

    group.controls.height.valueChanges
      .pipe(
        debounceTime(100),
        takeWhile(() => !!group)
      )
      .subscribe(val => {
        group.controls.width.updateValueAndValidity({ emitEvent: false });
      });

    group.controls.width.valueChanges
      .pipe(
        debounceTime(100),
        takeWhile(() => !!group)
      )
      .subscribe(val => {
        group.controls.height.updateValueAndValidity({ emitEvent: false });
      });

    group.controls.orientation.valueChanges
      .pipe(
        debounceTime(100),
        takeWhile(() => !!group)
      )
      .subscribe(val => {
        this.currentPanelsState = this.getCurrentPanelsState();
      });

    group.valueChanges
      .pipe(
        debounceTime(500),
        takeWhile(() => !!group)
      )
      .subscribe(val => {
        const panel = this._panels.find(p => p.uuid === val.uuid);
        _.merge(panel, val);
      });

    return group;
  }

  ngAfterViewInit(): void {
    this.eventManagerService.addCuttingPanel$.pipe(takeUntil(this.unsubscribeAll$)).subscribe(() => {
      this.addPanelAndSelectLast();
    });
  }

  private uniquePosValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const parent = control.parent as UntypedFormGroup;
      const forbidden =
        parent &&
        this._panels.filter(p => p.position.trim() === control.value.trim() && parent.controls.uuid.value !== p.uuid)
          .length;
      return forbidden ? { notUnique: { value: control.value } } : null;
    };
  }

  ngOnInit(): void {
    // if (this.adminMode) {
    //   this.cuttingPanelListService.getOrderAttachmentsForUser(this.orderId);
    // } else {
    //   this.cuttingPanelListService.getOrderAttachmentsForClient(this.orderId);
    // }

    if (!this.adminMode) {
      this.cuttingPanelListService.onClientOptionsChanged.subscribe((co: CuttingClientOptions) => {
        this.setDisplayednamesNamesDescriptionColumns(co.panelTableFields);
        this.editPanelPosition = co.editPanelPosition;
      });
    } else {
      const fields: PanelTableField[] = [
        {
          visible: true,
          displayName: 'Поз',
          name: 'position',
          description: 'Позиция',
        },
        {
          visible: true,
          displayName: 'Наим',
          name: 'name',
          description: 'Наименование панели',
        },
        {
          visible: true,
          displayName: 'Длина',
          name: 'height',
          description: 'Длина панели',
        },
        {
          visible: true,
          displayName: 'Ширина',
          name: 'width',
          description: 'Ширина панели',
        },
        {
          visible: true,
          displayName: 'Кол-во',
          name: 'count',
          description: 'Количество',
        },
        {
          visible: true,
          displayName: 'Текстура задана',
          name: 'orientation',
          description: 'Учитывать текстуру. Не вращать деталь в раскрое.',
        },
        {
          visible: true,
          displayName: 'L1',
          name: 'l1',
          description: 'Облицовка сверху',
        },
        {
          visible: true,
          displayName: 'L2',
          name: 'l2',
          description: 'Облицовка снизу',
        },
        {
          visible: true,
          displayName: 'W1',
          name: 'w1',
          description: 'Облицовка слева',
        },
        {
          visible: true,
          displayName: 'W2',
          name: 'w2',
          description: 'Облицовка справа',
        },
        {
          visible: true,
          displayName: 'Вложение',
          name: 'attachment',
          description: 'Вложение',
        },
        {
          visible: true,
          displayName: 'Обработка углов',
          name: 'editedAngles',
          description: 'Наличие углов с обработкой',
        },
        {
          visible: true,
          displayName: 'Паз',
          name: 'slotCount',
          description: 'Количество пазов на панели',
        },
        {
          visible: true,
          displayName: 'Наличие присадки',
          name: 'fastenersExists',
          description: 'Наличие присадки',
        },
        {
          visible: true,
          displayName: 'Облицовка пласти',
          name: 'faceBandingExists',
          description: 'Наличие облицовки пласти',
        },
        {
          visible: true,
          displayName: 'Обработка',
          name: 'outlineEditor',
          description: 'Редактор контура',
        },
        {
          visible: true,
          displayName: 'Прим',
          name: 'remark',
          description: 'Примечание',
        },
        { visible: true, displayName: '', name: 'delete', description: '' },
      ];

      this.setDisplayednamesNamesDescriptionColumns(fields);
    }
  }

  getDisplayedColumns(): string[] {
    let dc: string[] = this.displayedColumns;

    if (
      this.mainMaterial.type === MaterialType.Linear &&
      !this.materialEditableWidth.has(this.mainMaterial.inMaterialBaseGroupId)
    ) {
      dc = dc.filter(c => c !== 'width');
    }

    if (this.mainMaterial.type === MaterialType.Linear) {
      dc = dc.filter(c => c !== 'orientation');
    }

    if (this.currentScreenWidth === 'xs' || this.currentScreenWidth === 'sm') {
      // gt-sm
      dc = dc.filter(
        c => ['remark', 'l1', 'l2', 'w1', 'w2', 'editedAngles', 'slotCount', 'fastenersExists'].indexOf(c) === -1
      );
    }

    if (!this.presetSettings.componentsAvailability.outlineEditor.outlineEditor) {
      dc = dc.filter(
        c =>
          [
            'l1',
            'l2',
            'w1',
            'w2',
            'editedAngles',
            'slotCount',
            'fastenersExists',
            'attachment',
            'outlineEditor',
          ].indexOf(c) === -1
      );
    }

    return dc.filter(c => (this.panelSetHasButts ? true : c !== 'l1' && c !== 'l2' && c !== 'w1' && c !== 'w2'));
  }

  async wrapButtAlongOfPanel(): Promise<void> {
    this.postforming =
      this.materialGroupsWithPostforming.get(this.mainMaterial.inMaterialBaseGroupId) || PostformingSideEnum.None;

    const butt = await this.selectButtFromMaterialBaseDialog();
    if (!_.isNil(butt)) {
      PanelUtilsHelper.applyButtsInPanel(this._panels, butt, this.postforming);
      this.setPanels(this._panels);
    }
  }

  openShapeThePanelAlongFaceBanding(): void {
    const elements: BottomSheetItem[] = [{ name: $localize`Добавить сверху` }, { name: $localize`Добавить снизу` }];
    let bottomSheetRef = this._bottomSheet.open(BottomSheetSelectorComponent, {
      data: { title: $localize`Выберите позицию пласти`, elementsList: elements } as BottomSheetSelectorData,
    });

    bottomSheetRef.afterDismissed().subscribe(async (result: BottomSheetItem) => {
      if (result && result.name === $localize`Добавить сверху`) {
        await this.wrapFaceBandingOfPanel('+');
      }
      if (result && result.name === $localize`Добавить снизу`) {
        await this.wrapFaceBandingOfPanel('-');
      }
      bottomSheetRef = null;
    });
  }

  deleteFaceBanding(): void {
    this.panelList.forEach(panel => {
      if (panel.panelOutline.faceBanding.length > 0) {
        PanelUtilsHelper.deleteFaceBandingInPanel(panel);
      }
    });
  }

  replaceFaceBanding(): void {
    let faceBandingReplaceDialogReg = this.dialogsService.open(FaceBandingReplacementCustomDialogComponent, {
      panelClass: 'face-banding-replacement-dialog',
      disableClose: false,
      width: '600px',
      data: {
        panels: this.panelList,
      } as FaceBandingReplacementData,
    });

    faceBandingReplaceDialogReg.afterClosed().subscribe((panels: CuttingPanel[]) => {
      if (panels) {
        this.setPanels(panels);
        this.panelListChange.emit(panels);
      }
      faceBandingReplaceDialogReg = null;
    });
  }

  async wrapFaceBandingOfPanel(pos: '+' | '-'): Promise<void> {
    const materials = await this.selectMaterialFromMaterialBaseDialog();
    if (materials.length > 0) {
      PanelUtilsHelper.applyFaceBandingInPanels(this._panels, materials[0], pos);
      this.setPanels(this._panels);
      this.snackBar.open($localize`Облицовка пласти успешно применена`);
    }
  }

  async selectButtFromMaterialBaseDialog(): Promise<CuttingButt | undefined> {
    const butt = await lastValueFrom(
      this.legacyDialogsService.component<MaterialBaseEdgeDialogData, CuttingButt>(
        MaterialBaseButtsDialogComponent,
        { showQuantityInStock: this.cuttingSettings.showQuantityInStock },
        1000
      )
    ).catch(() => {
      return undefined;
    });

    return butt;
  }

  async selectMaterialFromMaterialBaseDialog(): Promise<CuttingMaterial[]> {
    const materials = await lastValueFrom(
      this.legacyDialogsService.component<CuttingMaterialBaseDialogData, CuttingMaterial[]>(
        CuttingMaterialBaseDialogComponent,
        {
          multiple: false,
          materialsToExclude: [],
          showQuantityInStock: this.cuttingSettings.showQuantityInStock,
        },
        1150
      )
    );
    return materials;
  }

  getSlotCount(panelUuid: string): number {
    const panel = this._panels.find(p => p.uuid === panelUuid);

    let res = panel.panelOutline.topSide.groove ? 1 : 0;
    res = res + (panel.panelOutline.rightSide.groove ? 1 : 0);
    res = res + (panel.panelOutline.bottomSide.groove ? 1 : 0);
    res = res + (panel.panelOutline.leftSide.groove ? 1 : 0);

    return res;
  }

  isAngleEditing(panelUuid: string): boolean {
    const panel = this._panels.find(p => p.uuid === panelUuid);

    return (
      panel.panelOutline.topLeft.mode !== AngleMode.None ||
      panel.panelOutline.topRight.mode !== AngleMode.None ||
      panel.panelOutline.bottomRight.mode !== AngleMode.None ||
      panel.panelOutline.bottomLeft.mode !== AngleMode.None
    );
  }

  isFastenersExists(panelUuid: string): boolean {
    const panel = this._panels.find(p => p.uuid === panelUuid);

    return (
      panel.panelOutline.topSide.customHoles.length !== 0 ||
      panel.panelOutline.rightSide.customHoles.length !== 0 ||
      panel.panelOutline.bottomSide.customHoles.length !== 0 ||
      panel.panelOutline.leftSide.customHoles.length !== 0 ||
      panel.panelOutline.topSide.fastenersSets.length !== 0 ||
      panel.panelOutline.rightSide.fastenersSets.length !== 0 ||
      panel.panelOutline.bottomSide.fastenersSets.length !== 0 ||
      panel.panelOutline.leftSide.fastenersSets.length !== 0
    );
  }

  isFaceBandingExists(panelUuid: string): boolean {
    const panel = this._panels.find(p => p.uuid === panelUuid);

    return panel.panelOutline.faceBanding.length > 0;
  }

  getError(errors: any): string {
    if (!errors) {
      return '';
    }

    if (errors.panelSize) {
      return $localize`Минимальный габаритный размер: ${errors.panelSize.overallSize.height.min}×${errors.panelSize.overallSize.width.min}.
       Максимальный габаритный размер: ${errors.panelSize.overallSize.height.max}×${errors.panelSize.overallSize.width.max}`;
    }

    if (errors.required) {
      return $localize`Поле не должно быть пустым`;
    }

    if (errors.number) {
      return $localize`Необходимо ввести число`;
    }

    if (errors.notUnique) {
      return $localize`Значение должно быть уникальным`;
    }

    if (errors.range) {
      return $localize`Значение должно быть от ${errors.range.value[0]} до ${errors.range.value[1]}`;
    }

    if (errors.maxNumberPrecision) {
      return $localize`Максимальное количество знаков после запятой — ${errors.maxNumberPrecision.max}`;
    }

    if (errors.maxlength) {
      return $localize`Максимальное количество символов — ${errors.maxlength.requiredLength}`;
    }
  }

  hasButts(panel: CuttingPanel): boolean {
    return panel.l1 || panel.l2 || panel.w1 || panel.w2 ? true : false;
  }

  hasCornersProcessting(panel: CuttingPanel): boolean {
    return (
      panel.panelOutline.topLeft.mode !== AngleMode.None ||
      panel.panelOutline.topRight.mode !== AngleMode.None ||
      panel.panelOutline.bottomRight.mode !== AngleMode.None ||
      panel.panelOutline.bottomLeft.mode !== AngleMode.None
    );
  }

  hasCutouts(panel: CuttingPanel): boolean {
    return !_.isEmpty(panel.panelOutline.cutouts);
  }

  setDisplayednamesNamesDescriptionColumns(optionsFields: PanelTableField[]): void {
    this.displayedColumns = [];
    optionsFields.forEach(item => {
      if (item.visible) {
        if (this.editableOrder || item.name !== 'delete') {
          this.displayedColumns.push(item.name);
        }
      }
    });
  }

  /************************************************************************/

  addPanelAndSelectLast(panel?: CuttingPanel): void {
    of(this.addPanel(panel))
      .pipe(
        takeUntil(this.unsubscribeAll$),
        delay(50),
        tap(() => this.focusLastPanel())
      )
      .subscribe();
  }

  addPanel(panel?: CuttingPanel): void {
    const newPanelIndex = this._panels.push(panel || new CuttingPanel()) - 1;

    if (this.mainMaterial.type === MaterialType.Linear) {
      this._panels[this._panels.length - 1].width = this.materialWidth;
    }

    this.panelListChange.emit(this._panels);
    this.setMissingPositions.emit(this._panels[newPanelIndex].uuid);

    const control = this.panel2FormGroup(this._panels[newPanelIndex]);
    this.panelsForm.push(control);

    this.table.renderRows();
  }

  deletePanel(panelUuid: string): void {
    _.remove(this._panels, p => p.uuid === panelUuid);
    this.panelsForm.removeAt((this.panelsForm.value as any[]).findIndex(p => p.uuid === panelUuid));

    this.panelListChange.emit(this._panels);

    this.table.renderRows();
  }

  private focusLastPanel(): void {
    const inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll('.mat-table tr .mat-column-height input');

    if (inputs.length > 0) {
      inputs[inputs.length - 1].focus();
      inputs[inputs.length - 1].select();
    }
  }

  /*******************************TOOLS*************************************/

  concatSameSizePanels(): void {
    if (this.concatSameSizePanelsForPanels(this._panels)) {
      this.setPanels(this._panels);
      this.panelListChange.emit(this._panels);

      this.table.renderRows();
    }
  }

  private concatSameSizePanelsForPanels(panels: CuttingPanel[]): boolean {
    let hasBeenChange = false;

    panels.forEach((iItem, i) => {
      for (let j = panels.length - 1; j > i; j--) {
        const jItem = panels[j];

        if (this.canJoinPanels(iItem, jItem)) {
          iItem.count += jItem.count;
          iItem.position += jItem.position ? ', ' + jItem.position : '';

          panels.splice(j, 1);

          hasBeenChange = true;
        }
      }
    });

    return hasBeenChange;
  }

  private canJoinPanels(firstPanel: CuttingPanel, secondPanel: CuttingPanel): boolean {
    const first = _.cloneDeep(firstPanel);
    const second = _.cloneDeep(secondPanel);

    first.position = '';
    first.count = 0;
    first.uuid = '';
    second.position = '';
    second.count = 0;
    second.uuid = '';

    return _.isEqual(first, second);
  }

  setPositionsInOrer(): void {
    this.setPosInOrder.emit();
    this.setPanels(this._panels);
  }

  saw2Finished(): void {
    this.saw2FinishedPanelsSize(this._panels);
    this.setPanels(this._panels);
    this.panelListChange.emit(this._panels);
  }

  finished2Saw(): void {
    this.finished2SawPanelsSize(this._panels);
    this.setPanels(this._panels);
    this.panelListChange.emit(this._panels);
  }

  countSaw2FinishedPanelsSize(): number {
    let count = 0;

    _.each(this._panels, p => {
      if (!p.options.recalculatedToFinishedSize) {
        count++;
      }
    });

    return count;
  }

  countFinished2SawPanelsSize(): number {
    let count = 0;

    _.each(this._panels, p => {
      if (p.options.recalculatedToFinishedSize) {
        count++;
      }
    });

    return count;
  }

  private saw2FinishedPanelsSize(panels: CuttingPanel[]): void {
    panels.forEach(p => {
      const hasButts = p.l1 || p.l2 || p.w1 || p.w2;
      if (!p.options.recalculatedToFinishedSize && hasButts) {
        p.height = p.height + ((p.w1 ? p.w1.thickness : 0) + (p.w2 ? p.w2.thickness : 0));
        p.width = p.width + ((p.l1 ? p.l1.thickness : 0) + (p.l2 ? p.l2.thickness : 0));

        if (!this.ignoreAllowanceWnenRecalculatingPanelSize) {
          p.height = p.height - ((p.w1 ? p.w1.allowance : 0) + (p.w2 ? p.w2.allowance : 0));
          p.width = p.width - ((p.l1 ? p.l1.allowance : 0) + (p.l2 ? p.l2.allowance : 0));
        }
        p.options.recalculatedToFinishedSize = true;
      }
    });
  }

  private finished2SawPanelsSize(panels: CuttingPanel[]): void {
    panels.forEach(p => {
      if (p.options.recalculatedToFinishedSize) {
        p.height = p.height - ((p.w1 ? p.w1.thickness : 0) + (p.w2 ? p.w2.thickness : 0));
        p.width = p.width - ((p.l1 ? p.l1.thickness : 0) + (p.l2 ? p.l2.thickness : 0));

        if (!this.ignoreAllowanceWnenRecalculatingPanelSize) {
          p.height = p.height + ((p.w1 ? p.w1.allowance : 0) - (p.w2 ? p.w2.allowance : 0));
          p.width = p.width + ((p.l1 ? p.l1.allowance : 0) - (p.l2 ? p.l2.allowance : 0));
        }
        p.options.recalculatedToFinishedSize = false;
      }
    });
  }

  handSizeChange(panel: CuttingPanel): void {
    panel.options.recalculatedToFinishedSize = false;
    this.panelListChange.emit(this._panels);
  }

  setOrientationToAllPanels(state: boolean): void {
    this._panels.forEach(item => {
      item.orientation = state;
    });

    this.currentPanelsState = state;

    this.setPanels(this._panels);

    this.panelListChange.emit(this._panels);
  }

  importCsv(): void {
    this.panelsImportCsvDialogRef = this.dialog.open(ImportFromCsvDialogComponent, {
      panelClass: 'import-csv-dialog',
      disableClose: false,
      width: '500px',
    });

    this.panelsImportCsvDialogRef.componentInstance.matInMatBaseId = this.inMatBaseIdMaterial;
    this.panelsImportCsvDialogRef.componentInstance.materialThickness = this.mainMaterial.thickness;

    this.panelsImportCsvDialogRef
      .afterClosed()
      .subscribe((res: { result: CuttingPanel[]; saveMode: ImportCsvSaveModeEnum }) => {
        if (res.result) {
          if (!this.editPanelPosition) {
            res.result.forEach(p => {
              p.position = '';
            });
          }

          const panelComparer = (one: CuttingPanel): ((two: CuttingPanel) => boolean) => {
            return (two: CuttingPanel): boolean => {
              return this.canJoinPanels(one, two);
            };
          };

          const setNewPositions = (panels: CuttingPanel[]): void => {
            if (!this.editPanelPosition) {
              panels.forEach(p => {
                this.setMissingPositions.emit(p.uuid);
              });
            }
          };

          const addedPanels: CuttingPanel[] = [];

          switch (res.saveMode) {
            default:
            case ImportCsvSaveModeEnum.CompleteAll:
              addedPanels.push(...res.result);
              break;

            case ImportCsvSaveModeEnum.OnlyNew:
              res.result.forEach(p => {
                if (this._panels.findIndex(panelComparer(p)) === -1) {
                  addedPanels.push(p);
                }
              });
              break;

            case ImportCsvSaveModeEnum.ReplaceExisting:
              this._panels = [];
              addedPanels.push(...res.result);
              break;
          }

          this.setPanels(this._panels.concat(addedPanels));
          this.panelListChange.emit(this._panels);
          setNewPositions(addedPanels);
        }
        this.panelsImportCsvDialogRef = null;
      });
  }

  exportCsv(): void {
    this.legacyDialogsService
      .component(CsvSchemaSelectorDialogComponent, null, 350)
      .pipe(
        filter(res => !!res),
        switchMap(res =>
          this.panelListDataService.downloadPanelsCsv(this.orderId, this.inMatBaseIdMaterial, this._panels, res.id)
        )
      )
      .subscribe();
  }

  getTextForChangeAllPanelOrientation(): string {
    return this.currentPanelsState ? $localize`Снять текстуру для панелей` : $localize`Установить текстуру для панелей`;
  }

  openFieldSettingsDialog(): void {
    this.fieldSettingsDialogRef = this.dialog.open(FieldSettingsComponent, {
      disableClose: false,
      panelClass: 'field-settings-dialog',
      width: '700px',
    });

    this.fieldSettingsDialogRef.afterClosed().subscribe((result: CuttingClientOptions) => {
      if (result !== undefined) {
        this.setDisplayednamesNamesDescriptionColumns(result.panelTableFields);
      }
      this.fieldSettingsDialogRef = null;
    });
  }

  openPanelMirroringDialog(panel: CuttingPanel): void {
    this.panelMirroringDialogRef = this.dialog.open(PanelMirroringDialogComponent, {
      panelClass: 'panel-mirroring-dialog',
      disableClose: true,
      width: '1000px',
    });

    this.panelMirroringDialogRef.componentInstance.panel = panel;

    this.panelMirroringDialogRef.afterClosed().subscribe((response: CuttingPanel | undefined) => {
      if (response) {
        this.addPanelAndSelectLast(response);
      }

      this.panelMirroringDialogRef = null;
    });
  }

  openOutlineEditorDialog(panelUuid: string, activeTab = OutlineEditorTabsEnum.Butts): void {
    const panel = this._panels.find(p => p.uuid === panelUuid);

    this.outlineEditorDialogRef = this.dialog.open(PanelOutlineEditorDialogComponent, {
      panelClass: 'panel-outline-editor-dialog',
      disableClose: true,
      minWidth: '1000px',
      width: '80vw',
    });

    this.outlineEditorDialogRef.componentInstance.adminMode = this.adminMode;
    this.outlineEditorDialogRef.componentInstance.orderId = this.orderId;
    this.outlineEditorDialogRef.componentInstance.activeTab = activeTab;
    this.outlineEditorDialogRef.componentInstance.matInMatBaseId = this.inMatBaseIdMaterial;
    this.outlineEditorDialogRef.componentInstance.panelMaterial = panel;
    this.outlineEditorDialogRef.componentInstance.mainMaterial = this.mainMaterial;
    this.outlineEditorDialogRef.componentInstance.editable = this.editableOrder;
    this.outlineEditorDialogRef.componentInstance.presetSettings = this.presetSettings;

    this.outlineEditorDialogRef.componentInstance.createMirrorPanel
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((response: CuttingPanel) => {
        this.openPanelMirroringDialog(response);
      });

    this.outlineEditorDialogRef.componentInstance.clonePanelEmit
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((response: CuttingPanel) => {
        this.clonePanel(response);
      });

    this.eventManagerService.lockAddCuttingPanel = true;

    this.outlineEditorDialogRef
      .afterClosed()
      .pipe(
        finalize(() => ((this.eventManagerService.lockAddCuttingPanel = false), (this.outlineEditorDialogRef = null)))
      )
      .subscribe(
        (
          response:
            | {
                panel: CuttingPanel;
                attachmentName: string;
              }
            | undefined
        ) => {
          if (response) {
            panel.attachmentName = response.attachmentName;
            panel.panelOutline = response.panel.panelOutline;
            const recalced = panel.options.recalculatedToFinishedSize;
            const rotated = panel.height !== response.panel.height && panel.width !== response.panel.width;

            if (recalced) {
              this.finished2SawPanelsSize([panel]);
            }

            if (rotated) {
              [panel.width, panel.height] = [panel.height, panel.width];
            }

            panel.l1 = response.panel.l1;
            panel.l2 = response.panel.l2;
            panel.w1 = response.panel.w1;
            panel.w2 = response.panel.w2;

            if (recalced) {
              this.saw2FinishedPanelsSize([panel]);
            }

            this.setPanels(this._panels);

            this.panelListChange.emit(this._panels);
          }

          this.outlineEditorDialogRef = null;
        }
      );
  }

  clonePanel(panel: CuttingPanel): void {
    const newPanel = _.cloneDeep(panel);

    newPanel.uuid = Guid.newGuid();
    newPanel.position = '';
    this.addPanelAndSelectLast(newPanel);
  }

  selectPanels(): void {
    this.displayedColumns.unshift('selection');
    this.selectionModeChange.next(true);
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this._panels.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle(): void {
    this.isAllSelected() ? this.selection.clear() : this._panels.forEach(row => this.selection.select(row.uuid));
  }

  cancelPanelSelection(): void {
    this.selection.clear();
    this.selectionModeChange.next(false);
    const fieldIndex = this.displayedColumns.indexOf('selection');

    if (fieldIndex !== -1) {
      this.displayedColumns.splice(fieldIndex, 1);
    }
  }

  clone(): void {
    this.cloneSelectedPanels.next(this._panels.filter(p => this.selection.selected.includes(p.uuid)));
  }

  sizesValidator(panelUuid: string, heightControlName: string, widthControlName: string): ValidatorFn {
    const panel = this._panels.find(p => p.uuid === panelUuid);
    return (formGroup: AbstractControl): ValidationErrors | null =>
      this.validateSizes(formGroup, panel, this.cuttingSettings.panels, heightControlName, widthControlName);
  }

  private validateSizes(
    formGroup: AbstractControl,
    panel: CuttingPanel,
    settings: PanelSettings,
    heightControlName: string,
    widthControlName: string
  ): ValidationErrors | null {
    const heightControl = formGroup.get(heightControlName);
    const widthControl = formGroup.get(widthControlName);

    [panel.height, panel.width] = [heightControl.value, widthControl.value];

    const { valid, widthRange, heightRange } =
      !settings || !panel
        ? { valid: true, widthRange: null, heightRange: null }
        : CuttingPanelHelper.checkPanelSize(panel, settings);

    if (!heightControl || !widthControl) {
      return null;
    }

    if (heightControl.hasError('panelSize')) {
      delete heightControl.errors.panelSize;
    }

    if (widthControl.hasError('panelSize')) {
      delete widthControl.errors.panelSize;
    }

    if (!valid) {
      const error = {
        panelSize: { overallSize: { height: heightRange, width: widthRange } },
      };

      heightControl.setErrors({ ...heightControl.errors, ...error }, { emitEvent: false });
      widthControl.setErrors({ ...widthControl.errors, ...error }, { emitEvent: false });

      return error;
    } else {
      return null;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
  }
}
